import cx from 'classnames';
import { HTMLAttributes } from 'react';

import { DesignSystemError, FallbackFunction } from '../../../types/FallbackFunction';
import { Anchor } from '../../anchor/Anchor';
import { IconStar } from '../../icons/material/rounded/Star';
import { IconStarHalf } from '../../icons/material/rounded/StarHalf';
import { IconStarOutlined } from '../../icons/material/rounded/StarOutlined';

import { RatingNumber } from './RatingNumber';
import { ReviewsCount } from './ReviewsCount';
import { Stars } from './Stars';

import style from './ReviewRating.module.css';

type IconComponent = typeof IconStar | typeof IconStarOutlined | typeof IconStarHalf;

const RATING_SCALE = {
    max: 5,
    min: 0,
} as const;

export function roundRatingForIcons(rating: number) {
    return Math.round(rating * 2) / 2;
}

export function roundRatingForText(rating: number) {
    return Math.round(rating * 10) / 10;
}

export interface ReviewRatingProps extends HTMLAttributes<HTMLDivElement> {
    align?: 'left' | 'center' | 'right';
    ariaLabel: (rating: { value: number; min: number; max: number }) => string;
    maxRating: number;
    ratingValue: number;
    minRating: number;
    fallback: FallbackFunction<ReviewRatingProps>;
    url?: string;
    obfuscate?: boolean;
    reviewsCount?: string;
    showRatingNumber?: boolean;
    size?: 'xxs' | 'xs' | 'small' | 'medium' | 'large';
    variant?: 'default' | 'single-star';
    inverse?: boolean;
    starColor?: 'yellow' | 'black';
    onClick?: () => void;
}

export function ReviewRating(props: ReviewRatingProps) {
    const {
        align = 'left',
        ariaLabel,
        className,
        obfuscate,
        reviewsCount,
        title,
        url,
        minRating,
        maxRating,
        ratingValue,
        size = 'medium',
        showRatingNumber = false,
        variant = 'default',
        inverse = false,
        starColor = 'yellow',
        fallback,
        onClick,
        ...htmlProps
    } = props;

    let error;

    if (ratingValue > maxRating) {
        error = new ReviewRatingValueGreaterThanMaxError(
            'Rating value is greater than max rating',
            {
                max: maxRating,
                value: ratingValue,
            },
        );
    }

    if (ratingValue < minRating) {
        error = new ReviewRatingValueLowerThanMinError('Rating value is lower than min rating', {
            min: minRating,
            value: ratingValue,
        });
    }

    if (error) {
        return fallback(error, props);
    }

    const rawRating = { min: minRating, max: maxRating, value: ratingValue };
    const rating = scaleRating(rawRating, RATING_SCALE.min, RATING_SCALE.max);
    const roundedRatingForIcons = roundRatingForIcons(rating.value);
    const roundedRatingForText = roundRatingForText(rating.value);
    const icons = iconsForValue(roundedRatingForIcons, rating.max);

    const iconsAndCount =
        variant === 'single-star' ? (
            <>
                <div className={style.singleStarIconAndRating}>
                    <Stars
                        variant={variant}
                        icons={icons}
                        ariaLabel={ariaLabel({
                            max: rating.max,
                            min: rating.min,
                            value: roundedRatingForText,
                        })}
                        size={size}
                        inverse={inverse}
                        starColor={starColor}
                    />
                    <RatingNumber
                        showRatingNumber={true}
                        ratingValue={roundedRatingForText}
                        size={size}
                        inverse={inverse}
                    />
                </div>
                <ReviewsCount reviewsCount={reviewsCount} size={size} inverse={inverse} />
            </>
        ) : (
            <>
                <RatingNumber
                    showRatingNumber={showRatingNumber}
                    ratingValue={roundedRatingForText}
                    size={size}
                    inverse={inverse}
                />
                <Stars
                    variant={variant}
                    icons={icons}
                    ariaLabel={ariaLabel({
                        max: rating.max,
                        min: rating.min,
                        value: roundedRatingForText,
                    })}
                    size={size}
                    inverse={inverse}
                    starColor={starColor}
                />
                <ReviewsCount reviewsCount={reviewsCount} size={size} inverse={inverse} />
            </>
        );

    return (
        <div
            data-testid="ReviewRating"
            {...htmlProps}
            className={cx(style.reviewRating, className, {
                [style.large]: size === 'large',
                [style.inverse]: inverse,
                [style.alignCenter]: align === 'center',
                [style.alignLeft]: align === 'left',
                [style.alignRight]: align === 'right',
            })}
        >
            {url ? (
                <Anchor
                    className={cx(style.iconsAndCount, {
                        [style.iconsAndCountSingleStar]: variant === 'single-star',
                        [style.iconsAndCountXXS]: size === 'xxs',
                        [style.iconsAndCountXS]: size === 'xs',
                    })}
                    href={url}
                    title={title}
                    obfuscate={obfuscate}
                    onClick={onClick}
                >
                    {iconsAndCount}
                </Anchor>
            ) : (
                <div
                    className={cx(style.iconsAndCount, {
                        [style.iconsAndCountSingleStar]: variant === 'single-star',
                        [style.iconsAndCountXXS]: size === 'xxs',
                        [style.iconsAndCountXS]: size === 'xs',
                    })}
                    onClick={onClick}
                >
                    {iconsAndCount}
                </div>
            )}
        </div>
    );
}

function iconsForValue(value: number, maxRating: number): IconComponent[] {
    const max = Math.floor(value);
    const icons: IconComponent[] = new Array(maxRating).fill(IconStarOutlined);

    for (let i = 0; i < max; i++) {
        icons[i] = IconStar;
    }

    if (value % 1 !== 0) {
        icons[max] = IconStarHalf;
    }

    return icons;
}

export function scaleRating(
    rating: { max: number; min: number; value: number },
    targetMin: number,
    targetMax: number,
): { max: number; min: number; value: number } {
    if (rating.max === targetMax && rating.min === targetMin) {
        return rating;
    }

    if (rating.max < rating.min) {
        return rating;
    }

    const ratio = (rating.value - rating.min) / (rating.max - rating.min);
    const value = ratio * (targetMax - targetMin) + targetMin;

    return {
        max: targetMax,
        min: targetMin,
        value,
    };
}

export class ReviewRatingValueGreaterThanMaxError extends DesignSystemError {
    constructor(
        message: string,
        data: {
            max: number;
            value: number;
        },
    ) {
        super(message, data);

        this.name = 'ReviewRatingValueGreaterThanMaxError';

        Object.setPrototypeOf(this, ReviewRatingValueGreaterThanMaxError.prototype);
    }
}

export class ReviewRatingValueLowerThanMinError extends DesignSystemError {
    constructor(
        message: string,
        data: {
            min: number;
            value: number;
        },
    ) {
        super(message, data);

        this.name = 'ReviewRatingValueLowerThanMinError';

        Object.setPrototypeOf(this, ReviewRatingValueLowerThanMinError.prototype);
    }
}
